/**
 * Project Favorites Component
 * Pin/unpin projects for quick access from dashboard
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Star, StarOff, Loader2, ChevronRight, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
const getAuthHeaders = () => {
  const token = localStorage.getItem('simpliview_token');
  const tenantId = localStorage.getItem('impersonated_tenant_id') || localStorage.getItem('simpliview_user_tenant_id');
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (tenantId) headers['X-Tenant-ID'] = tenantId;
  return headers;
};

// Health indicator badge
const HealthBadge = ({ health }) => {
  const colors = {
    green: 'bg-green-100 text-green-700',
    amber: 'bg-amber-100 text-amber-700',
    red: 'bg-red-100 text-red-700',
  };
  
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[health] || 'bg-gray-100 text-gray-600'}`}>
      {health || 'N/A'}
    </span>
  );
};

// Single favorite item
const FavoriteItem = ({ favorite, onRemove, onClick }) => {
  const [removing, setRemoving] = useState(false);

  const handleRemove = async (e) => {
    e.stopPropagation();
    setRemoving(true);
    await onRemove(favorite);
    setRemoving(false);
  };

  const project = favorite.project;

  return (
    <div 
      className="flex items-center justify-between p-3 bg-white border rounded-lg hover:border-blue-300 hover:shadow-sm cursor-pointer transition-all group"
      onClick={() => onClick(favorite)}
      data-testid={`favorite-project-${favorite.item_id}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <Star className="w-4 h-4 text-amber-500 flex-shrink-0" />
        <div className="min-w-0">
          <p className="font-medium text-gray-900 truncate">
            {project?.name || favorite.item_name}
          </p>
          {project && (
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs text-gray-500 capitalize">{project.status}</span>
              {project.health && <HealthBadge health={project.health} />}
            </div>
          )}
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        <button
          onClick={handleRemove}
          disabled={removing}
          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-all"
          title="Remove from favorites"
        >
          {removing ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <StarOff className="w-4 h-4" />
          )}
        </button>
        <ChevronRight className="w-4 h-4 text-gray-400" />
      </div>
    </div>
  );
};

// Add to favorites button (for use in project lists)
export const AddToFavoritesButton = ({ project, onToggle }) => {
  const { user, tenantId: authTenantId } = useAuth();
  const tenantId = localStorage.getItem('impersonated_tenant_id') || authTenantId;
  const userId = user?.id;
  
  const [isFavorite, setIsFavorite] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const checkFavorite = async () => {
      if (!project?.id || !userId) return;
      
      try {
        const res = await axios.get(
          `${API_BASE}/favorites/check/project/${project.id}?tenant_id=${tenantId}&user_id=${userId}`,
          { headers: getAuthHeaders() }
        );
        setIsFavorite(res.data.is_favorite);
      } catch (err) {
        console.debug('Failed to check favorite status');
      }
    };
    
    checkFavorite();
  }, [project?.id, userId, tenantId]);

  const handleToggle = async (e) => {
    e.stopPropagation();
    if (!project?.id || !userId) return;
    
    setLoading(true);
    try {
      if (isFavorite) {
        await axios.delete(
          `${API_BASE}/favorites/project/${project.id}?tenant_id=${tenantId}&user_id=${userId}`,
          { headers: getAuthHeaders() }
        );
        setIsFavorite(false);
        toast.success('Removed from favorites');
      } else {
        await axios.post(
          `${API_BASE}/favorites?tenant_id=${tenantId}&user_id=${userId}`,
          {
            item_type: 'project',
            item_id: project.id,
            item_name: project.name
          },
          { headers: getAuthHeaders() }
        );
        setIsFavorite(true);
        toast.success('Added to favorites');
      }
      onToggle?.();
    } catch (err) {
      toast.error('Failed to update favorites');
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleToggle}
      disabled={loading}
      className={`p-1.5 rounded transition-colors ${
        isFavorite 
          ? 'text-amber-500 hover:text-amber-600 hover:bg-amber-50' 
          : 'text-gray-400 hover:text-amber-500 hover:bg-amber-50'
      }`}
      title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
      data-testid={`favorite-toggle-${project?.id}`}
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : isFavorite ? (
        <Star className="w-4 h-4 fill-current" />
      ) : (
        <Star className="w-4 h-4" />
      )}
    </button>
  );
};

// Main favorites panel
const ProjectFavoritesPanel = ({ onProjectClick, compact = false }) => {
  const navigate = useNavigate();
  const { user, tenantId: authTenantId } = useAuth();
  const tenantId = localStorage.getItem('impersonated_tenant_id') || authTenantId;
  const userId = user?.id;
  
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadFavorites = useCallback(async () => {
    if (!userId || !tenantId) return;
    
    try {
      setLoading(true);
      const res = await axios.get(
        `${API_BASE}/favorites/projects?tenant_id=${tenantId}&user_id=${userId}`,
        { headers: getAuthHeaders() }
      );
      setFavorites(res.data.favorites || []);
    } catch (err) {
      console.error('Failed to load favorites:', err);
    } finally {
      setLoading(false);
    }
  }, [userId, tenantId]);

  useEffect(() => {
    loadFavorites();
  }, [loadFavorites]);

  const handleRemove = async (favorite) => {
    try {
      await axios.delete(
        `${API_BASE}/favorites/project/${favorite.item_id}?tenant_id=${tenantId}&user_id=${userId}`,
        { headers: getAuthHeaders() }
      );
      setFavorites(prev => prev.filter(f => f.id !== favorite.id));
      toast.success('Removed from favorites');
    } catch (err) {
      toast.error('Failed to remove from favorites');
    }
  };

  const handleClick = (favorite) => {
    if (onProjectClick) {
      onProjectClick(favorite.item_id, favorite.project);
    } else {
      navigate(`/projects/${favorite.item_id}/dashboard`);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border rounded-lg p-4" data-testid="favorites-panel">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
        </div>
      </div>
    );
  }

  if (favorites.length === 0) {
    return (
      <div className="bg-white border rounded-lg p-4" data-testid="favorites-panel">
        <div className="flex items-center gap-2 mb-3">
          <Star className="w-5 h-5 text-amber-500" />
          <h3 className="font-semibold text-gray-900">Favorite Projects</h3>
        </div>
        <div className="text-center py-6 text-gray-500">
          <Star className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No favorites yet</p>
          <p className="text-xs mt-1">Star projects for quick access</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border rounded-lg p-4" data-testid="favorites-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Star className="w-5 h-5 text-amber-500" />
          <h3 className="font-semibold text-gray-900">Favorite Projects</h3>
        </div>
        <span className="text-xs text-gray-500">{favorites.length} pinned</span>
      </div>
      
      <div className={`space-y-2 ${compact ? 'max-h-64 overflow-y-auto' : ''}`}>
        {favorites.map((fav) => (
          <FavoriteItem
            key={fav.id}
            favorite={fav}
            onRemove={handleRemove}
            onClick={handleClick}
          />
        ))}
      </div>
    </div>
  );
};

export default ProjectFavoritesPanel;
